import Select, {
  components,
  DropdownIndicatorProps,
  OptionProps,
  SingleValue,
  SingleValueProps,
} from 'react-select';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

import indicator from '@/assets/icons/language-selector/fluent_ios-arrow-24-filled.svg';

import { LanguageOption } from '@/utils/LanguageContext/LanguageContext';

import { useScreenType } from '@/hooks/useScreenType';
import { useLanguage } from '@/hooks/useLanguage';

import styles from './LanguageSelect.module.scss';

const CustomDropdownIndicator = (props: DropdownIndicatorProps<LanguageOption, false>) => {
  const { selectProps } = props;
  if (selectProps.menuIsOpen) {
    return null;
  }

  return (
    <components.DropdownIndicator {...props}>
      <div className={styles.customDropdownIndicator}>
        <img src={indicator} alt="Indicator" />
      </div>
    </components.DropdownIndicator>
  );
};

type CustomSingleValueProps = SingleValueProps<LanguageOption, false> & {
  isForFooter?: boolean;
};

function CustomSingleValue({ data, isForFooter }: CustomSingleValueProps) {
  return (
    <div
      className={clsx(styles.customSingleValue, {
        [styles.customSingleValue_forFooter]: isForFooter,
      })}
    >
      <div className={styles.customSingleValue__flag}>
        <img src={data.flag} alt="Flag" />
      </div>
      <div className={styles.customSingleValue__label}>
        {isForFooter ? data.fullLabel : data.label}
      </div>
    </div>
  );
}

type CustomOptionProps = OptionProps<LanguageOption, false> & {
  isForFooter?: boolean;
};

function CustomOption({ data, innerRef, innerProps, isForFooter }: CustomOptionProps) {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={clsx(styles.customOption, {
        [styles.customOption_forFooter]: isForFooter,
      })}
    >
      <div className={styles.customSingleValue__flag}>
        <img src={data.flag} alt="Flag" />
      </div>
      <div className={styles.customOption__label}>{isForFooter ? data.fullLabel : data.label}</div>
    </div>
  );
}

interface LanguageSelectProps {
  isForFooter?: boolean;
  className?: string;
}

export function LanguageSelect({ isForFooter = false, className }: LanguageSelectProps) {
  const navigate = useNavigate();
  const { selectedLanguage, setLanguage, languageOptions } = useLanguage();
  const { screenType } = useScreenType();
  const isMobileDevice = screenType === 'tablet' || screenType === 'mobile';

  const handleChange = (selectedOption: SingleValue<LanguageOption>) => {
    if (selectedOption) {
      setLanguage(selectedOption);
      navigate(selectedOption.value === 'uk' ? '/' : `/${selectedOption.value}`);
    }
  };

  const filteredOptions = languageOptions.filter(
    (option) => option.value !== selectedLanguage.value,
  );

  const getBackgroundColor = (isMobileDevice: boolean, isForFooter: boolean) => {
    if (isForFooter) return '#373737';
    return isMobileDevice ? '#f5f5f5' : 'rgba(255, 255, 255, 0.1)';
  };

  const getControlColor = (isMenuOpen: boolean, isMobileDevice: boolean, isForFooter: boolean) => {
    if (isMenuOpen) {
      if (isForFooter) return '#ffffff';
      return isMobileDevice ? '#7b7b7b' : '#ffffff';
    }

    return '';
  };

  return (
    <Select
      className={clsx(styles.languageSelect, className)}
      unstyled
      isSearchable={false}
      isClearable={false}
      options={filteredOptions}
      value={selectedLanguage}
      onChange={handleChange}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
        SingleValue: (props) => <CustomSingleValue {...props} isForFooter={isForFooter} />,
        Option: (props) => <CustomOption {...props} isForFooter={isForFooter} />,
      }}
      styles={{
        control: (base, state) => ({
          ...base,
          padding: isForFooter ? '5px 10px' : '3px 5px',
          minHeight: 'auto',
          gap: '7px',
          backgroundColor: getBackgroundColor(isMobileDevice, isForFooter),
          borderRadius: state.menuIsOpen ? '7px 7px 0 0' : '7px',
          color: getControlColor(state.menuIsOpen, isMobileDevice, isForFooter),
          transition: 'all none',
          cursor: 'pointer',
        }),
        menu: (base) => ({
          ...base,
          backgroundColor: getBackgroundColor(isMobileDevice, isForFooter),
          borderRadius: '0 0 7px 7px',
          overflow: 'hidden',
        }),
      }}
    />
  );
}
